import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { NGXLogger } from 'ngx-logger';

import { assertExists } from 'common';
import {
  SearchOptions,
  SearchPrompt,
  SearchProvider,
  createApiSearchPromptString,
} from 'models';
import { ApplicationQuery } from 'src/app/state/application/application.query';
import { ArchivesQuery } from 'src/app/state/archives/archives.query';
import { SearchesQuery } from 'src/app/state/searches/searches.query';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter } from 'rxjs';
import { SearchesService } from 'src/app/state/searches/searches.service';
import { ViewTabItem } from '../../models/view-tab-item';

/**
 * Tab View.
 */
@UntilDestroy()
@Component({
  selector: 'app-tab-view',
  templateUrl: './tab-view.component.html',
  styleUrls: ['./tab-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TabViewComponent implements OnInit {
  /** Active tab. */
  @Input()
  activeTab: ViewTabItem;
  /** Active tab emitter. */
  @Output()
  activeTabChange = new EventEmitter<ViewTabItem>();
  /** Search prompts from the user's search. */
  @Input()
  searchPrompts: SearchPrompt[];
  /** Selected tab index. */
  selectedIndex: number;
  /** Current set of view tabs. */
  viewTabs: ViewTabItem[];

  private defaultSearchOptionsForCount: SearchOptions = Object.freeze({
    page: 1,
    countOnly: false, // The important part.
    recordsPerPage: this.appQuery.archiveResultsPerPage,
    searchCriteria: '',
    sort: '',
    tabId: 0,
    targetArchiveId: 0,
  });

  constructor(
    private archivesQuery: ArchivesQuery,
    private searchesQuery: SearchesQuery,
    private appQuery: ApplicationQuery,
    private searchService: SearchesService,
    private logger: NGXLogger
  ) {}

  /**
   *  Gets the tab count string to be displayed.
   *
   * @param tabCount Tab count.
   * @returns A string to be displayed as the tab count.
   */
  getTabCountString(tabCount: number): string {
    return tabCount >= this.appQuery.archiveResultsPerPage
      ? `${this.appQuery.archiveResultsPerPage}+`
      : tabCount.toString();
  }

  ngOnInit(): void {
    this.logger.debug('Tab View: Initializing.');
    this.loadTabCounts();
  }

  /**
   * Event handler for tab change.
   *
   * Sets the active tab to the selected index.
   *
   * @param value Value.
   */
  onTabChanged(value: number): void {
    this.activeTab = this.viewTabs[value];
    this.activeTabChange.emit(this.activeTab);
    this.logger.debug('Tab View: Selected tab changed:', this.activeTab);
  }

  /**
   * Run searches to get result counts for each tab.
   *
   * We skip the first tab, since that tab will get
   * a full search run on it for the grid, and it would be redundant.
   */
  private loadTabCounts() {
    this.searchesQuery.searchRouteParams$
      .pipe(
        untilDestroyed(this),
        filter(() => {
          return (
            this.archivesQuery.activeHasViewTabs &&
            this.searchesQuery.active.settings.displayViewTabs
          );
        })
      )
      .subscribe({
        next: ([databaseId, archiveId, searchId, searchPrompts, viewTabId]) => {
          try {
            assertExists(databaseId);
            const archive = this.archivesQuery.getEntity(archiveId);
            assertExists(archive);
            // When the archive changes, create view tab items.
            this.viewTabs = archive.viewTabs.map(
              (t) => new ViewTabItem(t, true)
            );
            const tabCount = this.viewTabs.length;
            this.logger.debug(
              `Tab View: Active archive has ${tabCount ? tabCount : 'no'} tabs.`
            );
            // Get counts for each tab.
            for (const [index, tab] of this.viewTabs.entries()) {
              if (index === 0 && !viewTabId) {
                // Set the first tab as active.
                this.activeTab = tab;
                this.selectedIndex = 0;
                this.activeTabChange.emit(tab);
                this.logger.debug('Tab View: Setting the first tab as active.');
                // Skip the counts for first tab, it will need a full search anyway.
                continue;
              }

              if (viewTabId === tab.description.id) {
                // Set the active tab from the route as active.
                this.activeTab = tab;
                this.selectedIndex = index;
                this.logger.debug(
                  'Tab view: setting tab with id {0} as active.',
                  tab.description.id
                );
                this.activeTabChange.emit(tab);
                // Skip the counts for active tab, it will need a full search anyway.
                continue;
              }

              // Build the search options.
              // TODO: We should probably have a CountSearchOptions type.
              const searchOptions: SearchOptions = {
                ...this.defaultSearchOptionsForCount,
                tabId: tab.description.id,
                targetArchiveId: this.archivesQuery.activeId,
                searchCriteria: createApiSearchPromptString(searchPrompts),
              };
              this.logger.debug(
                'Tab View: Running count search for: ',
                this.searchesQuery.active,
                '...with options:',
                searchOptions
              );
              // Execute the count search.
              assertExists(searchId);
              const search = this.searchesQuery.getEntity(searchId);
              assertExists(search);
              this.searchService.run(search, searchOptions, true).subscribe({
                next: (response) => {
                  // Push the count result to the observable, and mark loading as complete.
                  this.logger.debug(
                    `Tab View: Setting count to "${response.count}" for "${tab.description.label}".`
                  );
                  tab.count = response.count;
                },
              });
            }
          } catch {
            this.logger.debug(
              'Tab View: No active archive, empty list of view tabs.'
            );
            this.viewTabs = [];
          }
        },
      });
  }
}
